import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });
const MODES = [
  { id: 'cash', label: 'Cash' },
  { id: 'upi', label: 'UPI' },
  { id: 'card', label: 'Card' },
  { id: 'netbanking', label: 'Net banking' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'other', label: 'Other' },
];

/** Collect or waive consultation fee for a queue appointment. */
export default function CollectPaymentPanel({ appointment, onDone, onCancel }) {
  const suggested = Number(appointment?.suggested_fee ?? 0);
  const [amount, setAmount] = useState(String(suggested));
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (waive = false) => {
    if (appointment?.kind !== 'queue') {
      toast.error('Confirm the booking into the queue first, then collect payment.');
      return;
    }
    setBusy(true);
    try {
      await ax.post(`/api/clinic-ops/queue/${appointment.id}/collect-payment`, {
        amount: waive ? 0 : Number(amount || 0),
        method,
        reference,
        waive,
      });
      toast.success(waive ? 'Fee waived — ready for doctor' : 'Payment collected');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment failed');
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 space-y-3" data-testid="collect-payment-panel">
      <p className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
        <IndianRupee className="h-4 w-4 text-amber-700" />
        Collect fee · {appointment?.patient_name}
      </p>
      <p className="text-[11px] text-zinc-600">
        Suggested ₹{suggested.toLocaleString('en-IN')} ({appointment?.fee_source || 'tariff'})
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)}
          data-testid="collect-amount" />
        <select value={method} onChange={(e) => setMethod(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-sm"
          data-testid="collect-method">
          {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>
      {method !== 'cash' && (
        <Input placeholder="Reference / UPI / last-4" value={reference}
          onChange={(e) => setReference(e.target.value)} data-testid="collect-ref" />
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => submit(false)} data-testid="collect-pay-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
          Collect ₹{Number(amount || 0).toLocaleString('en-IN')}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => submit(true)} data-testid="collect-waive-btn">
          Waive fee
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
